import { ReactNode } from "react";
import { Button, ButtonProps } from "components/DesignSystem/Button/Button";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { CaretDown } from "components/icons/CaretDown";
import { AddExistingRegistrationModal } from "pages/SalesTax/SalesTaxRegistrations/AddExistingRegistrationModal";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { useInitiateRegistrationForStateMutation } from "store/apis/salesTaxRegistrationDetails";
import { BackendError } from "types/utils/error";
import { getBillingCartUrl } from "util/getBillingCartUrl";

type RegisterStateDropdownProps = {
  entityId: string;
  stateId: string;
  stateName: string;
  triggerLabel?: ReactNode;
  triggerCustomType?: ButtonProps["customType"];
  block?: boolean;
  onOpenChange?: (open: boolean) => void;
};

export const RegisterStateDropdown = ({
  entityId,
  stateId,
  stateName,
  triggerLabel = "Register",
  triggerCustomType = "primary",
  block,
  onOpenChange,
}: RegisterStateDropdownProps) => {
  const { alertToast } = useToast();
  const { uuid: groupId } = useCurrentGroupContext();
  const { isAdmin } = useRoleBasedView();

  const alreadyRegisteredModal = useModal();

  const [initiateRegistrationForState, { isLoading }] =
    useInitiateRegistrationForStateMutation();

  const handleAddNewRegistration = async () => {
    try {
      await initiateRegistrationForState({ entityId, stateId }).unwrap();
      const cartUrl = getBillingCartUrl({
        isAdmin: Boolean(isAdmin),
        groupId,
        entityId,
      });
      window.open(cartUrl, "_blank");
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  return (
    <>
      <Dropdown.Root onOpenChange={onOpenChange}>
        <Dropdown.Trigger asChild className={block ? "t-w-full" : undefined}>
          <Button
            customType={triggerCustomType}
            size="small"
            rightIcon={<CaretDown />}
            isLoading={isLoading}
            disabled={isLoading}
            block={block}
          >
            {triggerLabel}
          </Button>
        </Dropdown.Trigger>
        <Dropdown.Portal>
          <Dropdown.Content align="end" sideOffset={4}>
            <Dropdown.Item onSelect={() => void handleAddNewRegistration()}>
              Add a new registration
            </Dropdown.Item>
            <Dropdown.Item onSelect={alreadyRegisteredModal.open}>
              Add an existing registration
            </Dropdown.Item>
          </Dropdown.Content>
        </Dropdown.Portal>
      </Dropdown.Root>
      {alreadyRegisteredModal.isOpen && (
        <AddExistingRegistrationModal
          onClose={alreadyRegisteredModal.close}
          initialRegistrationData={{
            state_registration_id: "",
            state: stateName,
            state_id: stateId,
          }}
        />
      )}
    </>
  );
};
